import { c as _c } from "react/compiler-runtime";
import { Image } from "@/components/Image";
import { Heading } from "@/components/ui/Heading";
import * as React from "react";
import cn from "twsx";

// Full-screen camera surface for guided photo capture: a live video feed or
// preview fills the screen under top and bottom scrims, with a step header,
// the step title, and a shutter row on top.
/** Use when a person takes a guided photo with the device camera. */
export function CameraCapture(t0) {
  const $ = _c(18);
  const {
    title,
    labelledBy,
    videoRef,
    previewSrc,
    previewAlt,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Heading level={1} className="sr-only">{title}</Heading>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== videoRef) {
    t2 = <video className="size-full object-cover" muted={true} playsInline={true} ref={videoRef} />;
    $[2] = videoRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== previewAlt || $[5] !== previewSrc) {
    t3 = previewSrc && <Image alt={previewAlt} className="object-cover" fill={true} sizes="100vw" src={previewSrc} unoptimized={true} />;
    $[4] = previewAlt;
    $[5] = previewSrc;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-0 bg-black/20" />;
    t5 = <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-black/80 to-transparent" />;
    t6 = <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/90 to-transparent" />;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t3) {
    t7 = <div aria-hidden="true" className="absolute inset-0">{t2}{t3}{t4}{t5}{t6}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== children || $[14] !== labelledBy || $[15] !== t1 || $[16] !== t7) {
    t8 = <main aria-labelledby={labelledBy} className="fixed inset-0 z-modal flex min-h-dvh flex-col overflow-hidden bg-black text-white">{t1}{t7}{children}</main>;
    $[13] = children;
    $[14] = labelledBy;
    $[15] = t1;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}

// Exit control, "n of total" and one progress segment per step.
export function CameraCaptureHeader(t0) {
  const $ = _c(22);
  const {
    exit,
    current,
    steps,
    progressLabel
  } = t0;
  let t1;
  if ($[0] !== steps) {
    t1 = steps.filter(Boolean);
    $[0] = steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const completed = t1.length;
  const t2 = current + 1;
  let t3;
  if ($[2] !== steps.length || $[3] !== t2) {
    t3 = <p className="text-xs font-medium text-white/70">{t2} of {steps.length}</p>;
    $[2] = steps.length;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = steps.length;
  let t5;
  if ($[5] !== current || $[6] !== steps) {
    let t6;
    if ($[8] !== current) {
      t6 = (done, index) => <span key={index} className={cn("h-1 flex-1 rounded-full", done ? "bg-white" : index === current ? "bg-white/45" : "bg-white/15")} />;
      $[8] = current;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = steps.map(t6);
    $[5] = current;
    $[6] = steps;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[10] !== completed || $[11] !== progressLabel || $[12] !== steps.length || $[13] !== t5) {
    t6 = <div aria-label={progressLabel} aria-valuemax={t4} aria-valuemin={0} aria-valuenow={completed} className="flex w-full max-w-64 gap-1" role="progressbar">{t5}</div>;
    $[10] = completed;
    $[11] = progressLabel;
    $[12] = steps.length;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t6) {
    t7 = <div className="flex min-w-0 flex-1 flex-col items-center gap-2">{t3}{t6}</div>;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div aria-hidden="true" className="size-10 shrink-0" />;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== exit || $[20] !== t7) {
    t9 = <header className="relative z-20 flex items-center gap-4 px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))]">{exit}{t7}{t8}</header>;
    $[19] = exit;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
export function CameraCaptureStep(t0) {
  const $ = _c(15);
  const {
    title,
    instructions,
    headingId,
    headingRef,
    children
  } = t0;
  let t1;
  if ($[0] !== headingId || $[1] !== headingRef || $[2] !== title) {
    t1 = <Heading level={2} size="lg" tone="inverse" id={headingId} ref={headingRef} tabIndex={-1}>{title}</Heading>;
    $[0] = headingId;
    $[1] = headingRef;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== instructions) {
    t2 = instructions && <p className="max-w-md text-sm leading-5 text-white/65">{instructions}</p>;
    $[4] = instructions;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className="relative z-10 flex shrink-0 flex-col items-center gap-1 px-6 pb-4 pt-2 text-center">{t1}{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex-1" />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children) {
    t5 = <div className="relative z-20 shrink-0 px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4">{children}</div>;
    $[10] = children;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t5) {
    t6 = <section className="flex min-h-0 flex-1 flex-col">{t3}{t4}{t5}</section>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

// `review` lays out two equal actions under a preview; `shoot` sets back,
// shutter and next on one row.
export function CameraCaptureControls(t0) {
  const $ = _c(5);
  const {
    layout,
    children
  } = t0;
  const t1 = layout === "review" ? "grid-cols-2" : "grid-cols-[1fr_auto_1fr] items-center gap-4 [&>*:first-child]:justify-self-start [&>*:last-child]:justify-self-end";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("mx-auto grid w-full max-w-md gap-3", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
export function CameraCaptureShutter(t0) {
  const $ = _c(11);
  const {
    label,
    onClick,
    disabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("flex size-[4.5rem] items-center justify-center rounded-full border-4 border-white bg-white/15", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white", "focus-visible:ring-offset-2 focus-visible:ring-offset-black", "disabled:pointer-events-none disabled:opacity-50");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="size-14 rounded-full bg-white" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== disabled || $[3] !== label || $[4] !== onClick) {
    t3 = <button aria-label={label} className={t1} disabled={disabled} onClick={onClick} type="button">{t2}</button>;
    $[2] = disabled;
    $[3] = label;
    $[4] = onClick;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== label) {
    t4 = <span aria-hidden="true" className="text-sm font-medium text-white/80">{label}</span>;
    $[6] = label;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="flex flex-col items-center gap-2">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}